import type { KeyboardEvent } from "react";

interface Option {
  value: string;
  label: string;
}

interface Props {
  fieldId?: string;
  label?: string;
  /** When the label is rendered elsewhere (e.g. with a help toggle), use this for radiogroup aria-label. */
  groupAriaLabel?: string;
  helper?: string;
  optionalLabel?: string;
  options: Option[];
  value: string;
  onChange: (v: string) => void;
  multi?: boolean;
}

export default function ButtonChoice({ fieldId, label, groupAriaLabel, helper, optionalLabel, options, value, onChange, multi = false }: Props) {
  const selected = multi ? value.split(",").filter(Boolean) : [value];

  const handleClick = (v: string) => {
    if (multi) {
      const current = value.split(",").filter(Boolean);
      const next = current.includes(v) ? current.filter((x) => x !== v) : [...current, v];
      onChange(next.join(","));
    } else {
      onChange(v === value ? "" : v);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, v: string) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick(v);
    }
  };

  return (
    <div id={fieldId}>
      {label && (
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[13px] font-semibold text-slate-500 uppercase tracking-wide">
            {label}
          </span>
          {optionalLabel && (
            <span className="text-[11px] text-slate-400 bg-slate-50 border border-slate-100 rounded-full px-2 py-0.5">
              {optionalLabel}
            </span>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={label || groupAriaLabel}>
        {options.map((opt) => {
          const active = selected.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              tabIndex={0}
              onClick={() => handleClick(opt.value)}
              onKeyDown={(e) => handleKeyDown(e, opt.value)}
              style={{ touchAction: "manipulation" }}
              className={`h-10 px-4 rounded-full text-sm font-medium border transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5 ${
                active
                  ? "bg-slate-500 border-slate-500 text-white"
                  : "bg-white border-slate-200 text-slate-700 hover:border-slate-400 hover:text-slate-600"
              }`}
            >
              <i
                className="ri-check-line text-xs"
                style={{
                  width: active ? "0.75rem" : "0",
                  overflow: "hidden",
                  opacity: active ? 1 : 0,
                  transition: "width 0.15s, opacity 0.15s",
                  display: "inline-block",
                }}
              />
              {opt.label}
            </button>
          );
        })}
      </div>
      {helper && <p className="text-[13px] text-slate-400 mt-1">{helper}</p>}
    </div>
  );
}
